import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import DatePicker from 'react-datepicker';
import api from '../../../utils/api';
import { ChecklistDisplay } from '../../Common/TaskChecklist';
import 'react-datepicker/dist/react-datepicker.css';
import './CleanerArchives.css';
import './AvailableTasks.css';

// ── Mapping helpers (mirrors mobile orderService.js) ──
const SERVICE_TYPES = { home: 'Home Cleaning', commercial: 'Commercial Cleaning', moveinout: 'Move-in/Move-out' };
const CLEANING_CATEGORIES = { standard: 'Standard Cleaning', main: 'Main Cleaning', adhoc: 'Ad hoc Cleaning' };
const KNOWN_AS_NEEDED = ['wipingFrames', 'limeShower', 'vacuumFurniture', 'cobwebs'];
const KNOWN_MAIN_EXTRAS = [
  'wipingFrames', 'wipingWindowEdges', 'wipingCeilingEdges', 'windowCleaning',
  'blindsCleaned', 'furnitureVacuumed', 'behindFurniture', 'cleaningWallsCeilings',
  'underCarpets', 'mirrorsGlass', 'ceilingLights', 'closedSpaces',
  'insideCupboards', 'woodenFloors', 'ovenFridge',
];
const KNOWN_ADHOC = ['standardWithout', 'vacuumAndFloor', 'bathroomAndFloor', 'largeHome'];
const KNOWN_EXTRA_TARGETED = ['animalHair', 'smoking'];
const EQUIPMENT_PREFIX = 'equipment:';
const EQUIPMENT_KEYS = ['cleaningAgents', 'cloth', 'vacuumCleaner', 'mop', 'specialProducts'];
const ALL_KNOWN = new Set([...KNOWN_AS_NEEDED, ...KNOWN_MAIN_EXTRAS, ...KNOWN_ADHOC, ...KNOWN_EXTRA_TARGETED]);

const mapServiceType = (cleaningType) => {
  if (cleaningType === 'residential') return 'home';
  if (cleaningType === 'move') return 'moveinout';
  if (cleaningType === 'deep') return 'home';
  return cleaningType;
};
const mapCleaningCategory = (cleaningType) => (cleaningType === 'deep' ? 'main' : 'standard');

const formatClientDisplay = (client) => {
  if (!client) return 'N/A';
  return client.name || '-';
};

const CleanerArchives = ({ userId }) => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [completedTasks, setCompletedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // Helper function to extract numeric part from task ID
  const getTaskIdNumber = (taskId) => {
    if (!taskId) return '';
    // If it's already a number (SQL Server), return it directly
    if (typeof taskId === 'number') {
      return taskId.toString();
    }
    // Convert to string if needed
    const idStr = String(taskId);
    // For MongoDB ObjectId (24 characters), return the last 6 characters
    if (idStr.length === 24) {
      return idStr.slice(-6);
    }
    // For other formats, try to extract number or return as is
    const match = idStr.match(/-(\d+)$/);
    return match ? match[1] : idStr;
  };

  useEffect(() => {
    fetchCompletedTasks();
  }, [userId, startDate, endDate]);

  const fetchCompletedTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // Add date filters
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      
      const response = await api.get('/tasks/completed', { params });
      let tasks = response.data.data || [];
      
      // Sort by date descending (latest first), then by createdAt
      tasks = tasks.sort((a, b) => {
        const dateA = new Date(a.date || a.createdAt);
        const dateB = new Date(b.date || b.createdAt);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      setCompletedTasks(tasks);
    } catch (error) {
      console.error('Error fetching completed tasks:', error);
      showToast(error.response?.data?.message || t('cleaner.errorFetchingTasks', 'Error fetching completed tasks. Please try again.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="cleaner-archives">
      <div className="cleaner-archives-header">
        <h2>{t('cleaner.myCompletedTasks', 'My Completed Tasks')}</h2>
        <div className="date-filter">
          <div className="date-filter-group">
            <label>{t('common.from', 'From')}:</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText={t('common.startDate', 'Start date')}
              dateFormat="dd,MM,yyyy"
              className="date-picker-input"
            />
          </div>
          <div className="date-filter-group">
            <label>{t('common.to', 'To')}:</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              placeholderText={t('common.endDate', 'End date')}
              dateFormat="dd,MM,yyyy"
              className="date-picker-input"
            />
          </div>
          {(startDate || endDate) && (
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
            >
              {t('common.clearFilter', 'Clear Filter')}
            </button>
          )}
        </div>
      </div>
      
      {completedTasks.length === 0 ? (
        <p className="no-tasks">{t('cleaner.noCompletedTasks', 'No completed tasks yet.')}</p>
      ) : (
        <div className="archives-table">
          <table className="table">
            <thead>
              <tr>
                <th>{t('admin.taskId', 'Task ID')}</th>
                <th>{t('cleaner.client', 'Client')}</th>
                <th>{t('auth.address', 'Address')}</th>
                <th>{t('client.date', 'Date')}</th>
                <th>{t('client.time', 'Time')}</th>
                <th>{t('cleaner.expectedHours', 'Expected Hours')}</th>
                <th>{t('cleaner.actualHours', 'Actual Hours')}</th>
                <th>{t('admin.type', 'Type')}</th>
                <th>{t('cleaner.clientRating', 'Client Rating')}</th>
                <th>{t('cleaner.clientComment', 'Client Comment')}</th>
                <th>{t('admin.action', 'Action')}</th>
              </tr>
            </thead>
            <tbody>
              {completedTasks.map(task => {
                const taskId = task._id || task.id;
                return (
                  <tr key={taskId}>
                    <td>{getTaskIdNumber(taskId)}</td>
                    <td>{formatClientDisplay(task.client)}</td>
                    <td>{task.address}</td>
                    <td>{new Date(task.date).toLocaleDateString('en-GB')}</td>
                    <td>{task.time}</td>
                    <td>{task.hours}</td>
                    <td>{task.actualHours || '-'}</td>
                    <td>{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</td>
                    <td>
                      {task.rating ? (
                        <span className="rating-display">
                          {'★'.repeat(task.rating)}
                          {'☆'.repeat(5 - task.rating)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{task.ratingComment || '-'}</td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setSelectedTask(task)}
                      >
                        {t('admin.details', 'Details')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedTask && (
        <div className="at-modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="at-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="at-modal-header">
              <h3>{t('admin.taskDetails', 'Task Details')} - #{getTaskIdNumber(selectedTask._id || selectedTask.id)}</h3>
              <button className="at-close-btn" onClick={() => setSelectedTask(null)}>&times;</button>
            </div>
            <div className="at-modal-body">
              {/* Status */}
              <div className="at-detail-status">
                <span className="at-status at-status-completed">completed</span>
              </div>

              {/* Service Type */}
              <div className="at-detail-section">
                <h4>{t('newOrder.serviceType', 'Service Type')}</h4>
                <p>{SERVICE_TYPES[mapServiceType(selectedTask.cleaningType)] || selectedTask.cleaningType}</p>
              </div>

              {/* Client Information */}
              {(selectedTask.client || selectedTask.clientId) && (
                <div className="at-detail-section">
                  <h4>{t('cleaner.clientInfo', 'Client Information')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">👤</span>
                    <span>{(selectedTask.client || selectedTask.clientId)?.name || 'N/A'}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">🏙️</span>
                    <span><strong>{t('auth.city', 'City')}:</strong> {selectedTask.client?.city || '-'}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📮</span>
                    <span><strong>{t('auth.zipCode', 'Zip Code')}:</strong> {selectedTask.client?.zipCode || '-'}</span>
                  </div>
                </div>
              )}

              {/* Title */}
              {selectedTask.title && (
                <div className="at-detail-section">
                  <h4>{t('orders.title', 'Title')}</h4>
                  <p>{selectedTask.title}</p>
                </div>
              )}

              {/* Booking Details */}
              <div className="at-detail-section">
                <h4>{t('newOrder.bookingDetails', 'Booking Details')}</h4>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📍</span>
                  <span>{selectedTask.address || '—'}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📅</span>
                  <span>{new Date(selectedTask.date).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">🕐</span>
                  <span>{selectedTask.allDay ? t('orders.allDay', 'All Day') : (selectedTask.time || '—')}{selectedTask.endTime ? ` – ${selectedTask.endTime}` : ''}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">⏳</span>
                  <span>{selectedTask.hours || '–'} {t('admin.hours', 'hours')}{selectedTask.actualHours ? ` (${t('cleaner.actualHours', 'Actual')}: ${selectedTask.actualHours})` : ''}</span>
                </div>
              </div>

              {/* Dynamic checklist-derived sections */}
              {(() => {
                const checklist = Array.isArray(selectedTask.checklist) ? selectedTask.checklist : [];
                const asNeeded = checklist.filter(i => KNOWN_AS_NEEDED.includes(i));
                const mainExtras = checklist.filter(i => KNOWN_MAIN_EXTRAS.includes(i));
                const adhoc = checklist.filter(i => KNOWN_ADHOC.includes(i));
                const equipmentSelected = EQUIPMENT_KEYS.filter(k => checklist.includes(`${EQUIPMENT_PREFIX}${k}`));
                const extraTargeted = [];
                if (checklist.includes('animalHair')) extraTargeted.push(t('newOrder.animalHair', 'Animal hair'));
                if (checklist.includes('smoking')) extraTargeted.push(t('newOrder.smoking', 'Smoking'));
                const remaining = checklist.filter(item => typeof item === 'string' && !ALL_KNOWN.has(item) && !item.startsWith(EQUIPMENT_PREFIX));

                const equipmentLabels = {
                  cleaningAgents: t('newOrder.cleaningAgents', 'Cleaning agents'),
                  cloth: t('newOrder.cloth', 'Cloth'),
                  vacuumCleaner: t('newOrder.vacuumCleaner', 'Vacuum cleaner'),
                  mop: t('newOrder.mop', 'Mop'),
                  specialProducts: t('newOrder.specialProducts', 'Special products'),
                };

                const renderChecklistSection = (items, title) => items.length > 0 ? (
                  <div className="at-detail-section" key={title}>
                    <h4>{title}</h4>
                    <ul className="at-detail-list">
                      {items.map((item, i) => <li key={i}>✅ {item}</li>)}
                    </ul>
                  </div>
                ) : null;

                return (
                  <>
                    {renderChecklistSection(asNeeded, t('newOrder.asNeeded', 'As Needed'))}
                    {renderChecklistSection(mainExtras, t('newOrder.mainCleaningExtras', 'Main Cleaning Extras'))}
                    {renderChecklistSection(adhoc, t('newOrder.adhocOptions', 'Ad hoc Options'))}
                    {equipmentSelected.length > 0 && (
                      <div className="at-detail-section">
                        <h4>{t('newOrder.equipment', 'Equipment')}</h4>
                        <ul className="at-detail-list">
                          {equipmentSelected.map((k) => <li key={k}>✅ {equipmentLabels[k] || k}</li>)}
                        </ul>
                      </div>
                    )}
                    {renderChecklistSection(extraTargeted, t('newOrder.extraTargeted', 'Extra Targeted'))}
                    {remaining.length > 0 && (
                      <div className="at-detail-section">
                        <h4>{t('client.whatNeedsToBeDone', 'What needs to be done')}</h4>
                        <ChecklistDisplay items={remaining} completed={selectedTask.checklistCompleted} />
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Comments */}
              {selectedTask.comments && (
                <div className="at-detail-section">
                  <h4>{t('newOrder.additionalComments', 'Comments')}</h4>
                  <div className="at-detail-comments">{selectedTask.comments}</div>
                </div>
              )}

              {/* Rating */}
              {selectedTask.rating && (
                <div className="at-detail-section">
                  <h4>{t('admin.ratingInformation', 'Rating Information')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">⭐</span>
                    <span>
                      <span className="rating-display">
                        {'★'.repeat(selectedTask.rating)}{'☆'.repeat(5 - selectedTask.rating)}
                      </span>
                      {' '}({selectedTask.rating}/5)
                    </span>
                  </div>
                  {selectedTask.ratingComment && (
                    <div className="at-detail-comments" style={{ marginTop: 8 }}>{selectedTask.ratingComment}</div>
                  )}
                </div>
              )}

              {/* Order Date */}
              {selectedTask.createdAt && (
                <div className="at-detail-meta">
                  <span className="at-detail-meta-label">{t('orders.orderDate', 'Order Date')}</span>
                  <span>{new Date(selectedTask.createdAt).toLocaleDateString('en-GB')}</span>
                </div>
              )}
            </div>
            <div className="at-modal-actions">
              <button className="btn btn-secondary" onClick={() => setSelectedTask(null)}>
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CleanerArchives;



